import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight, ChevronDown, Check, Copy, ExternalLink, CheckCircle2,
  Circle, Code, Loader2, Shield, X, Target, ArrowRight, Zap, Play,
  AlertTriangle, TrendingUp, Globe, Mic,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Optimize() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enginesData, setEnginesData] = useState(null);
  const [simulator, setSimulator] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [feedAllLoading, setFeedAllLoading] = useState(false);
  const [feedAllResult, setFeedAllResult] = useState(null);
  const [expandedEngine, setExpandedEngine] = useState(null);
  const [schema, setSchema] = useState(null);
  const [showSchema, setShowSchema] = useState(false);
  const [schemaCopied, setSchemaCopied] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [engRes, simRes, corrRes] = await Promise.all([
          axios.get(`${API}/ai-engines/status`, { withCredentials: true }),
          axios.get(`${API}/score-simulator`, { withCredentials: true }),
          axios.get(`${API}/corrections`, { withCredentials: true }),
        ]);
        if (engRes.data?.has_business) setEnginesData(engRes.data);
        if (simRes.data?.has_business) setSimulator(simRes.data);
        setCorrections(corrRes.data.corrections || []);

        // Auto-expand first unfed engine
        if (engRes.data?.engines) {
          const firstUnfed = Object.entries(engRes.data.engines).find(([, e]) => !e.fed);
          if (firstUnfed) setExpandedEngine(firstUnfed[0]);
        }
      } catch (e) {
        if (e.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [navigate]);

  const handleFeedAll = async () => {
    setFeedAllLoading(true);
    try {
      const res = await axios.post(`${API}/ai-engines/feed-all`, {}, { withCredentials: true });
      setFeedAllResult(res.data);
      // Refresh engines
      const engRes = await axios.get(`${API}/ai-engines/status`, { withCredentials: true });
      if (engRes.data?.has_business) setEnginesData(engRes.data);
    } catch {}
    setFeedAllLoading(false);
  };

  const handleMarkSubmitted = async (dirId) => {
    try {
      await axios.post(`${API}/submissions/${dirId}`, { status: 'submitted' }, { withCredentials: true });
      const engRes = await axios.get(`${API}/ai-engines/status`, { withCredentials: true });
      if (engRes.data?.has_business) setEnginesData(engRes.data);
    } catch {}
  };

  const fetchSchema = async () => {
    try {
      const res = await axios.get(`${API}/submissions/schema`, { withCredentials: true });
      setSchema(res.data);
      setShowSchema(true);
    } catch {}
  };

  const handleCopySchema = async () => {
    if (!schema) return;
    try {
      await navigator.clipboard.writeText(schema.embed_code);
      setSchemaCopied(true);
      setTimeout(() => setSchemaCopied(false), 3000);
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const fed = enginesData?.engines_fed || 0;
  const total = enginesData?.engines_total || 9;
  const engines = enginesData?.engines || {};

  return (
    <AppShell active="optimize">
      <div className="p-5 max-w-3xl mx-auto pb-28" data-testid="optimize-page">
        {/* Header */}
        <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight mb-1">
          Help AI Mention You
        </h1>
        <p className="text-sm text-[#5C5347] mb-4 leading-relaxed">
          You can't submit directly to ChatGPT or Claude. But you can control what they find about you. We handle the rest.
        </p>

        {/* Engines That Mention You Tracker */}
        <div className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] mb-4" data-testid="engine-coverage">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-[#5C5347]">Engines That Mention You</span>
            <span className="font-semibold text-[#3C3226]">{fed} of {total} engines fed</span>
          </div>
          <div className="h-3 bg-[#F7F4EF] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1D6B4F] rounded-none transition-all duration-700"
              style={{ width: `${total > 0 ? (fed / total) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* C4: GBP Deep-Link Actions */}
        <GBPActions />

        {/* Score Simulator */}
        {simulator && simulator.total_boost > 0 && (
          <div className="p-4 rounded-none mb-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }} data-testid="score-simulator">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target size={14} className="text-[#1D6B4F]" />
                <span className="text-xs font-medium text-[#3C3226]">Score Simulator</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-base font-bold text-[#5C5347]">{simulator.current_score}</span>
                <ArrowRight size={12} className="text-[#1D6B4F]" />
                <span className="text-base font-bold text-[#1D6B4F]">{simulator.projected_score}</span>
              </div>
            </div>
            <p className="text-xs text-[#5C5347]">
              Complete all actions for <span className="font-semibold text-[#1D6B4F]">+{simulator.total_boost} points</span>
            </p>
          </div>
        )}

        {/* Schema.org Quick Action */}
        <button
          onClick={fetchSchema}
          className="w-full p-3 rounded-none bg-[#3C3226]/5 border border-[#3C3226]/10 mb-4 flex items-center gap-3 hover:bg-[#3C3226]/10 transition-colors text-left"
          data-testid="generate-schema-btn"
        >
          <Code size={16} className="text-[#1D6B4F] flex-shrink-0" />
          <span className="text-xs font-medium text-[#3C3226] flex-1">Get Schema.org JSON-LD markup</span>
          <ChevronRight size={14} className="text-[#5C5347]" />
        </button>

        {/* Schema Code Block */}
        <AnimatePresence>
          {showSchema && schema && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mb-4">
              <div className="p-4 rounded-none bg-[#3C3226] text-[#F0EBE3]" data-testid="schema-code-block">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#F0EBE3]/60">Schema.org JSON-LD</span>
                  <div className="flex gap-2">
                    <button onClick={handleCopySchema} className="flex items-center gap-1 text-xs text-[#F0EBE3]/60 hover:text-[#F0EBE3]" data-testid="copy-schema-btn">
                      {schemaCopied ? <Check size={12} /> : <Copy size={12} />} {schemaCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <button onClick={() => setShowSchema(false)} className="text-[#F0EBE3]/40 hover:text-[#F0EBE3]"><X size={14} /></button>
                  </div>
                </div>
                <pre className="text-xs text-[#1D6B4F] overflow-x-auto max-h-48">{schema.embed_code}</pre>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Engine Sections */}
        <div className="space-y-2 mb-5" data-testid="engine-list">
          {Object.entries(engines).map(([engineId, engine]) => {
            const isExpanded = expandedEngine === engineId;
            const isFed = engine.fed;
            const timeline = engine.timeline || [];

            return (
              <motion.div
                key={engineId}
                layout
                className={`rounded-none border overflow-hidden ${
                  isFed ? 'bg-[#1D6B4F]/3 border-[#1D6B4F]/15' : 'bg-[#F7F4EF] border-[#D4C9B8]'
                }`}
                data-testid={`engine-${engineId}`}
              >
                <button
                  onClick={() => setExpandedEngine(isExpanded ? null : engineId)}
                  className="w-full p-3.5 flex items-center gap-3 text-left"
                >
                  {/* Engine icon */}
                  <div
                    className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 text-base"
                    style={{ backgroundColor: `${engine.color}12`, borderLeft: `3px solid ${engine.color}` }}
                  >
                    {engine.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#3C3226]">{engine.name}</p>
                      <span className="text-[10px] text-[#5C5347]">{engine.provider}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] text-[#5C5347] truncate">{engine.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Engine Health Timeline sparkline */}
                    {timeline.length >= 2 && (
                      <Sparkline data={timeline} color={engine.color} />
                    )}
                    {isFed ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-[#1D6B4F] bg-[#1D6B4F]/10 px-2 py-0.5 rounded-full">
                        <CheckCircle2 size={10} /> Fed
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium text-[#C67A5C]">
                        {engine.sources_done}/{engine.sources_total}
                      </span>
                    )}
                    <ChevronDown size={14} className={`text-[#5C5347] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Expanded source list */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3.5 pb-3 space-y-1.5">
                        {engine.sources.map(src => (
                          <div
                            key={src.key}
                            className={`flex items-center gap-2.5 p-2.5 rounded-sm ${
                              src.status === 'done' ? 'bg-[#1D6B4F]/5' :
                              src.status === 'incomplete' ? 'bg-[#EF9F27]/5' : 'bg-[#F7F4EF]'
                            }`}
                            data-testid={`source-${engineId}-${src.key}`}
                          >
                            {/* Status indicator */}
                            {src.status === 'done' ? (
                              <CheckCircle2 size={14} className="text-[#1D6B4F] flex-shrink-0" />
                            ) : src.status === 'incomplete' ? (
                              <AlertTriangle size={14} className="text-[#EF9F27] flex-shrink-0" />
                            ) : (
                              <Circle size={14} className="text-[#E24B4A] flex-shrink-0" />
                            )}

                            <span className="text-xs text-[#3C3226] flex-1">{src.name}</span>

                            {/* Action button */}
                            {src.status !== 'done' && src.action && (
                              src.action.action === 'external' ? (
                                <a
                                  href={src.action.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-none bg-[#1D6B4F] text-[#F0EBE3] text-[10px] font-medium hover:bg-[#16563F] transition-colors"
                                >
                                  {src.action.label} <ExternalLink size={9} />
                                </a>
                              ) : (
                                <button
                                  onClick={() => navigate(src.action.path)}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-none bg-[#1D6B4F] text-[#F0EBE3] text-[10px] font-medium hover:bg-[#16563F] transition-colors"
                                >
                                  {src.action.label} <ArrowRight size={9} />
                                </button>
                              )
                            )}

                            {src.auto && src.status === 'done' && (
                              <span className="text-[9px] text-[#1D6B4F] font-bold">AUTO</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Feed All Engines Master Button */}
        {!feedAllResult && (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={handleFeedAll}
            disabled={feedAllLoading}
            className="w-full p-5 rounded-sm bg-[#C67A5C] text-[#F0EBE3] flex items-center gap-4 hover:bg-[#B3694C] transition-all active:scale-[0.98] mb-5 text-left disabled:opacity-70"
            data-testid="feed-all-btn"
          >
            <div className="w-12 h-12 rounded-none bg-[#F0EBE3]/15 flex items-center justify-center flex-shrink-0">
              {feedAllLoading
                ? <Loader2 size={22} className="text-[#F0EBE3] animate-spin" />
                : <Zap size={22} className="text-[#F0EBE3]" />}
            </div>
            <div className="flex-1">
              <span className="text-base font-medium block">
                {feedAllLoading ? 'Generating content...' : 'Feed all engines at once'}
              </span>
              <span className="text-sm text-[#F0EBE3]/70">
                Auto-generate blogs, social posts, X/Twitter content + guided manual steps
              </span>
            </div>
            <Play size={18} className="text-[#F0EBE3]/60 flex-shrink-0" />
          </motion.button>
        )}

        {/* Feed All Results */}
        <AnimatePresence>
          {feedAllResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-sm bg-[#1D6B4F]/5 border border-[#1D6B4F]/20 mb-5"
              data-testid="feed-all-results"
            >
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-[#1D6B4F]" />
                <h3 className="text-sm font-medium text-[#3C3226]">Content generated!</h3>
              </div>
              <div className="space-y-1.5 mb-3">
                {feedAllResult.auto_completed?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-[#5C5347]">
                    <CheckCircle2 size={12} className="text-[#1D6B4F] flex-shrink-0" />
                    <span>{a.count} {a.type.replace('_', ' ')} ready</span>
                    <span className="text-[10px] text-[#5C5347]/60">feeds {a.engines.join(', ')}</span>
                  </div>
                ))}
              </div>
              {feedAllResult.manual_items?.length > 0 && (
                <>
                  <p className="text-xs font-medium text-[#C67A5C] mb-2">
                    Manual steps (~{feedAllResult.estimated_manual_minutes} min):
                  </p>
                  <div className="space-y-1.5">
                    {feedAllResult.manual_items.map((m, i) => (
                      <button
                        key={i}
                        onClick={() => m.external ? window.open(m.path, '_blank') : navigate(m.path)}
                        className="w-full text-left p-2.5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8] flex items-center gap-2 hover:bg-[#EDE8DF] transition-colors"
                      >
                        <Circle size={10} className="text-[#C67A5C] flex-shrink-0" />
                        <span className="text-xs text-[#3C3226] flex-1">{m.task}</span>
                        <span className="text-[10px] text-[#5C5347]">~{m.minutes} min</span>
                        <ChevronRight size={12} className="text-[#5C5347]" />
                      </button>
                    ))}
                  </div>
                </>
              )}
              <p className="text-xs text-[#1D6B4F] font-medium mt-3">
                Estimated score impact: +{feedAllResult.estimated_score_impact} points
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Corrections Section */}
        <CorrectionsSection corrections={corrections} setCorrections={setCorrections} />
      </div>
    </AppShell>
  );
}

/* ── Engine Health Timeline Sparkline ── */
function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.sources_done);
  const max = Math.max(...values, 1);
  const w = 48, h = 18, pad = 1;
  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = pad + (1 - v / max) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  const lastVal = values[values.length - 1];
  const prevVal = values[values.length - 2];
  const trending = lastVal >= prevVal;
  return (
    <div className="flex items-center gap-1" data-testid="engine-sparkline">
      <svg width={w} height={h} className="flex-shrink-0">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.7"
        />
        <circle cx={parseFloat(points.split(' ').pop().split(',')[0])} cy={parseFloat(points.split(' ').pop().split(',')[1])} r="2" fill={color} />
      </svg>
      <TrendingUp size={8} className={`flex-shrink-0 ${trending ? 'text-[#1D6B4F]' : 'text-[#E24B4A] rotate-180'}`} />
    </div>
  );
}

/* ── Corrections (kept from F8) ── */
function CorrectionsSection({ corrections, setCorrections }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    engine: 'chatgpt', query: '', incorrect_info: '', correct_info: '', correction_type: 'wrong_info',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!formData.incorrect_info || !formData.correct_info) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/corrections`, formData, { withCredentials: true });
      setCorrections(prev => [{ ...res.data, ...formData, correction_id: res.data.correction_id, created_at: new Date().toISOString() }, ...prev]);
      setShowForm(false);
      setFormData({ engine: 'chatgpt', query: '', incorrect_info: '', correct_info: '', correction_type: 'wrong_info' });
    } catch {}
    setSubmitting(false);
  };

  const engines = [
    { id: 'chatgpt', name: 'ChatGPT' }, { id: 'claude', name: 'Claude' },
    { id: 'gemini', name: 'Gemini' }, { id: 'perplexity', name: 'Perplexity' },
    { id: 'copilot', name: 'Copilot' }, { id: 'grok', name: 'Grok' },
    { id: 'google_ai', name: 'Google AI' },
  ];
  const correctionTypes = [
    { id: 'wrong_info', name: 'Wrong information' }, { id: 'outdated', name: 'Outdated data' },
    { id: 'missing', name: 'Missing entirely' }, { id: 'competitor_confused', name: 'Confused with competitor' },
    { id: 'wrong_location', name: 'Wrong location' },
  ];

  return (
    <div className="mt-4" data-testid="corrections-section">
      <button
        onClick={() => setShowForm(!showForm)}
        className="w-full p-3.5 rounded-none bg-[#C67A5C]/5 border border-[#C67A5C]/15 flex items-center gap-3 hover:bg-[#C67A5C]/10 transition-colors text-left mb-3"
        data-testid="flag-incorrect-btn"
      >
        <Shield size={16} className="text-[#C67A5C] flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-medium text-[#3C3226]">Flag Incorrect AI Info</p>
          <p className="text-[10px] text-[#5C5347]">Report and auto-generate corrective content</p>
        </div>
        <ChevronDown size={14} className={`text-[#5C5347] transition-transform ${showForm ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-3">
            <div className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] space-y-3" data-testid="correction-form">
              <div>
                <label className="text-xs font-medium text-[#5C5347] mb-1.5 block">Which AI engine?</label>
                <div className="flex flex-wrap gap-1.5">
                  {engines.map(e => (
                    <button key={e.id} onClick={() => setFormData(d => ({ ...d, engine: e.id }))}
                      className={`px-3 py-1.5 rounded-none text-xs font-medium border transition-all ${formData.engine === e.id ? 'bg-[#1D6B4F] text-[#F0EBE3] border-[#1D6B4F]' : 'bg-[#F7F4EF] text-[#5C5347] border-[#D4C9B8]'}`}
                      data-testid={`engine-${e.id}`}
                    >{e.name}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5347] mb-1.5 block">Type of issue</label>
                <div className="flex flex-wrap gap-1.5">
                  {correctionTypes.map(ct => (
                    <button key={ct.id} onClick={() => setFormData(d => ({ ...d, correction_type: ct.id }))}
                      className={`px-3 py-1.5 rounded-none text-xs font-medium border transition-all ${formData.correction_type === ct.id ? 'bg-[#C67A5C] text-[#F0EBE3] border-[#C67A5C]' : 'bg-[#F7F4EF] text-[#5C5347] border-[#D4C9B8]'}`}
                    >{ct.name}</button>
                  ))}
                </div>
              </div>
              <input type="text" value={formData.query} onChange={e => setFormData(d => ({ ...d, query: e.target.value }))}
                placeholder='What did you search?' className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226]" data-testid="correction-query" />
              <textarea value={formData.incorrect_info} onChange={e => setFormData(d => ({ ...d, incorrect_info: e.target.value }))}
                placeholder="What did the AI say (wrong)?" rows={2} className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-[#3C3226]" data-testid="correction-incorrect" />
              <textarea value={formData.correct_info} onChange={e => setFormData(d => ({ ...d, correct_info: e.target.value }))}
                placeholder="What is the correct information?" rows={2} className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-[#3C3226]" data-testid="correction-correct" />
              <button onClick={handleSubmit} disabled={submitting || !formData.incorrect_info || !formData.correct_info}
                className="w-full rounded-none bg-[#C67A5C] hover:bg-[#B3694C] disabled:opacity-40 text-[#F0EBE3] py-3 text-sm font-medium transition-all flex items-center justify-center gap-2"
                data-testid="submit-correction-btn"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
                File Correction & Generate Fix
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {corrections.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[#5C5347]">Active corrections ({corrections.length})</p>
          {corrections.map((c, i) => (
            <div key={c.correction_id || i} className="p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8]" data-testid={`correction-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-none bg-[#C67A5C]/10 text-[#C67A5C]">{c.engine}</span>
                <span className="text-[10px] text-[#5C5347]">{c.correction_type?.replace(/_/g, ' ')}</span>
                <span className={`ml-auto text-[10px] font-medium ${c.status === 'resolved' ? 'text-[#1D6B4F]' : 'text-[#EF9F27]'}`}>{c.status || 'in_progress'}</span>
              </div>
              {c.incorrect_info && <p className="text-[10px] text-[#E24B4A] line-through">{c.incorrect_info}</p>}
              {c.correct_info && <p className="text-[10px] text-[#1D6B4F]">{c.correct_info}</p>}
              {c.corrective_content && (
                <details className="mt-1">
                  <summary className="text-[10px] text-[#1D6B4F] cursor-pointer font-medium">View corrective content</summary>
                  <p className="mt-1 text-[10px] text-[#5C5347] leading-relaxed whitespace-pre-wrap bg-[#F7F4EF] p-2 rounded-none">{c.corrective_content}</p>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* C4: GBP Deep-Link Actions */
function GBPActions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/gbp-actions`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data?.has_business) return null;

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] mb-4" style={{ border: '1px solid #D4C9B8' }} data-testid="gbp-actions">
      <div className="flex items-center gap-2 mb-4">
        <Globe size={16} strokeWidth={1.5} style={{ color: '#1D6B4F' }} />
        <h3 className="text-[13px] font-medium" style={{ color: '#3C3226' }}>Google Business Quick Fixes</h3>
      </div>
      <div className="space-y-2">
        {data.actions.map(action => (
          <a
            key={action.key}
            href={action.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-none transition-colors hover:bg-[#EDE8DF] group"
            style={{ border: '1px solid #D4C9B8' }}
            data-testid={`gbp-${action.key}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[13px] font-medium" style={{ color: '#3C3226' }}>{action.label}</span>
                {action.priority === 'high' && (
                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(212,68,68,0.08)', color: '#D44' }}>High</span>
                )}
              </div>
              <span className="text-[11px]" style={{ color: '#5C5347' }}>{action.description}</span>
            </div>
            <ExternalLink size={14} className="flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: '#1D6B4F' }} />
          </a>
        ))}
      </div>
    </div>
  );
}
